import React, { useEffect, useState, useRef } from "react";
import { connect, useSelector } from "react-redux";
import { PageHeader } from "@ant-design/pro-components";
import {
  Layout,
  Breadcrumb,
  Typography,
  Space,
  Modal,
  Tooltip,
  Empty,
  Button,
  message,
  Avatar,
  Badge,
  ConfigProvider,
  Row,
  Col,
} from "antd";
import { ProTable } from "@ant-design/pro-components";
import {
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  FileSyncOutlined,
  GlobalOutlined,
  DesktopOutlined,
  MobileOutlined,
  CloudServerOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import ptBRIntl from "antd/lib/locale/pt_BR";

import _ from "lodash";
import moment from "moment";
import "moment/locale/pt-br";
import "reactjs-popup/dist/index.css";

/**
 * Actions
 */
import { activityAPI } from "../../../services/actions/activity";
import { importApi } from "../../../services/actions/import";
import { revertActivity, revertActivityFlag } from "auth/actions/activitieActions";
import { defaultTablePagination } from "utils/table-pagination";

/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";
import ActivitiesFilters from "../../../components/Activities/ActivitiesFilters";
import ActivityDetails from "../../../components/Activities/ActivityDetails";

/**
 * Hooks & Constants
 */
import { useActivitiesFilters } from "../../../hooks/useActivitiesFilters";
import { actionMap, getActionText, getPageText } from "./constants";

/**
 * Utils
 */
import { resolveUserNames } from "utils/resolveUserNameActivity";

const { Content } = Layout;
const { Text } = Typography;
const { confirm } = Modal;

const normalizePageKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const extractImportQueueId = (value) => {
  if (!value || typeof value !== "object") return null;

  if (value.queueId) return value.queueId;
  if (value.importQueueId) return value.importQueueId;

  if (value.queue && typeof value.queue === "object" && value.queue._id) {
    return value.queue._id;
  }

  if (value.metadata) {
    const metadataQueueId = extractImportQueueId(value.metadata);
    if (metadataQueueId) return metadataQueueId;
  }

  if (value.payload) {
    const payloadQueueId = extractImportQueueId(value.payload);
    if (payloadQueueId) return payloadQueueId;
  }

  return null;
};

const getImportQueueIdFromRecord = (record) => {
  const rawDataItems = Array.isArray(record?.rawData)
    ? record.rawData
    : _.compact([record?.rawData]);

  const candidates = [record, record?.metadata, ...rawDataItems];

  for (const candidate of candidates) {
    const queueId = extractImportQueueId(candidate);
    if (queueId) return queueId;
  }

  return null;
};

const getPossibleImportFormNames = (formName) => {
  const normalizedName = String(formName || "").trim();
  if (!normalizedName) return [];

  const names = [normalizedName];

  if (normalizedName.startsWith("obj_")) {
    names.push(normalizedName.replace(/^obj_/, ""));
  } else {
    names.push(`obj_${normalizedName}`);
  }

  return _.uniq(names);
};

const getImportHistoryItems = (response) => {
  if (Array.isArray(response?.message)) return response.message;
  if (Array.isArray(response)) return response;
  return [];
};

const getImportHistoryItemId = (item) =>
  item?._id || item?.queueId || item?.id || null;

const getImportHistoryItemCreatedAt = (item) =>
  item?.createdAt || item?.timestamp || item?.startedAt || null;

const getImportHistoryItemCreatedBy = (item) =>
  item?.createdBy || item?.created_by || item?.userId || item?.user?.id || null;

const getImportHistoryItemTotalCount = (item) => {
  const explicitTotal =
    item?.totalCount ??
    item?.totalRecords ??
    item?.total ??
    item?.processedRecords ??
    item?.successCount;

  if (_.isNumber(explicitTotal)) return explicitTotal;

  if (
    _.isNumber(item?.successCount) ||
    _.isNumber(item?.errorCount)
  ) {
    return Number(item?.successCount || 0) + Number(item?.errorCount || 0);
  }

  if (Array.isArray(item?.documents)) return item.documents.length;
  if (Array.isArray(item?.successDocuments)) return item.successDocuments.length;

  return null;
};

const scoreImportHistoryItem = (item, record) => {
  const itemId = getImportHistoryItemId(item);
  if (!itemId) return Number.NEGATIVE_INFINITY;

  let score = 0;
  const recordCreatedBy = record?.createdBy || null;
  const itemCreatedBy = getImportHistoryItemCreatedBy(item);
  const recordTotalCount = record?.metadata?.totalCount ?? null;
  const itemTotalCount = getImportHistoryItemTotalCount(item);

  if (recordCreatedBy && itemCreatedBy && String(recordCreatedBy) === String(itemCreatedBy)) {
    score += 1000;
  }

  if (
    _.isNumber(recordTotalCount) &&
    _.isNumber(itemTotalCount)
  ) {
    if (Number(recordTotalCount) === Number(itemTotalCount)) {
      score += 500;
    } else {
      score -= Math.abs(Number(recordTotalCount) - Number(itemTotalCount));
    }
  }

  const recordTime = record?.timestamp ? moment(record.timestamp) : null;
  const itemTimeRaw = getImportHistoryItemCreatedAt(item);
  const itemTime = itemTimeRaw ? moment(itemTimeRaw) : null;

  if (recordTime?.isValid() && itemTime?.isValid()) {
    const diffSeconds = Math.abs(recordTime.diff(itemTime, "seconds"));
    score -= diffSeconds;

    if (diffSeconds <= 5) score += 300;
    else if (diffSeconds <= 60) score += 150;
    else if (diffSeconds <= 300) score += 50;
  }

  if (item?.status === "finished" || item?.status === "error" || item?.status === "failed") {
    score += 25;
  }

  return score;
};

const resolveImportQueueIdFromHistory = async (record, unitId) => {
  const formNames = getPossibleImportFormNames(record?.form?.name || record?.form);
  if (!unitId || formNames.length === 0) return null;

  const historyLists = await Promise.all(
    formNames.map(async (formName) => {
      try {
        const response = await importApi.filesHistoryByForm(formName, unitId);
        return getImportHistoryItems(response);
      } catch (error) {
        return [];
      }
    })
  );

  const candidates = _.uniqBy(historyLists.flat(), (item) => getImportHistoryItemId(item));
  if (candidates.length === 0) return null;

  const rankedCandidates = candidates
    .map((item) => ({
      item,
      score: scoreImportHistoryItem(item, record),
    }))
    .sort((a, b) => b.score - a.score);

  const bestCandidate = rankedCandidates[0];
  return bestCandidate ? getImportHistoryItemId(bestCandidate.item) : null;
};

function AllActivities({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const actionRef = useRef();
  const [userNames, setUserNames] = useState(new Map());
  const [activeKeys, setActiveKeys] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [totalActivities, setTotalActivities] = useState(0);
  const company = useSelector((state) => state.user.company);

  // Use the custom hook for filters
  const filters = useActivitiesFilters();

  const routes = [
    {
      breadcrumbName: "Atividades",
      href: "/atividades/todas",
    },
    {
      breadcrumbName: "todas",
    },
  ];

  /**
   * Reverte atividade
   * @param {*} record
   */
  function handleRevert(record) {
    const { page, action } = record;
    const normalizedPage = normalizePageKey(getPageText(page) || page);
    const config =
      (actionMap[action] && actionMap[action][normalizedPage]) || {};
    const isImportActivity = config.isImport || normalizedPage === "importacao";
    const messageKey = `revert-activity-${record.id}`;
    const runRevert = async () => {
      message.loading({
        content: "Revertendo atividade...",
        key: messageKey,
        duration: 0,
      });

      try {
        if (isImportActivity) {
          const queueId =
            getImportQueueIdFromRecord(record) ||
            (await resolveImportQueueIdFromHistory(record, company?.unitId));

          if (!queueId) {
            console.error("Import activity missing queueId", {
              record,
              page,
              normalizedPage,
              action,
              metadata: record?.metadata,
              rawData: record?.rawData,
              ids: record?.ids,
              unitId: company?.unitId,
              changesLength: Array.isArray(record?.changes) ? record.changes.length : null,
            });
            throw new Error(
              "NÃ£o foi possÃ­vel identificar o lote da importaÃ§Ã£o para reverter todos os registros."
            );
          }

          const { status, data } = await importApi.rollbackImport(queueId, user.sub);

          if (status !== 200) {
            throw new Error(
              typeof data === "string"
                ? data
                : "A reversÃ£o da importaÃ§Ã£o falhou."
            );
          }
        } else {
          await revertActivity({
            formName: record.form?.name || record.form,
            activitieId: record.id,
            page: config.page || page,
            action: config.action || action,
            ids: record.changes,
            useBin: config.useBin || false,
            useActivitie: config.useActivitie || false,
            isImport: config.isImport || false,
            isObjDataUpdate: config.isObjDataUpdate || false,
            isObjDataRecover: config.isObjDataRecover || false,
            createdBy: user.sub,
          });
        }

        await revertActivityFlag(record.id);
        message.success({
          content: "Atividade revertida com sucesso.",
          key: messageKey,
          duration: 3,
        });
        actionRef.current?.reload();
      } catch (error) {
        message.error({
          content: error?.message || "NÃ£o foi possÃ­vel reverter a atividade.",
          key: messageKey,
          duration: 5,
        });
      }
    };

    confirm({
      title: `Você realmente deseja reverter?`,
      icon: <ExclamationCircleOutlined />,
      content: "Essa ação não pode ser desfeita.",
      okText: "Confirmar",
      onOk() {
        void runRevert();
      },
      cancelText: "Cancelar",
    });
  }

  const columns = [
    {
      title: "Formulário",
      dataIndex: "form",
      key: "form",
      render: (text, record) => {
        const formNameRaw = text?.name || text;
        if (!formNameRaw) return null;

        let formNames = [];
        if (typeof formNameRaw === 'string') {
          formNames = formNameRaw.split(',').map(s => s.trim()).filter(Boolean);
        } else if (Array.isArray(formNameRaw)) {
          formNames = formNameRaw;
        } else {
          formNames = [formNameRaw];
        }

        const MAX_VISIBLE = 2;
        const visibleForms = formNames.slice(0, MAX_VISIBLE);
        const hiddenFormsCount = formNames.length - MAX_VISIBLE;

        return (
          <Space wrap size={[0, 4]} style={{ maxWidth: '450px' }}>
            <Avatar
              size="small"
              icon={<FileSyncOutlined />}
              style={{
                backgroundColor: record.createdWith ? "darkviolet" : "#0e9c84",
                marginRight: 4
              }}
            />
            {visibleForms.map((name, index) => {
              const displayName = typeof name === 'string' ? name.replace("obj_", "") : name;
              const isLastVisible = index === visibleForms.length - 1;
              const hasComma = !isLastVisible || hiddenFormsCount > 0;
              return (
                <span key={index} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Button
                    type="link"
                    style={{
                      color: record.createdWith ? "darkviolet" : "#0e9c84",
                      padding: 0,
                      height: 'auto',
                      whiteSpace: 'nowrap',
                      textAlign: 'left'
                    }}
                    onClick={() => navigate(`/formularios/registros/obj_${displayName}`)}
                  >
                    {displayName}
                  </Button>
                  {hasComma && <span style={{ marginRight: 4, color: '#666' }}>,</span>}
                </span>
              );
            })}
            {hiddenFormsCount > 0 && (
              <Tooltip title={formNames.slice(MAX_VISIBLE).map(n => typeof n === 'string' ? n.replace("obj_", "") : n).join(", ")}>
                <span style={{ color: '#666', fontSize: '14px', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  e mais {hiddenFormsCount} {hiddenFormsCount === 1 ? 'formulário' : 'formulários'}
                </span>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Origem",
      tooltip: "Página ou módulo de origem da atividade",
      dataIndex: "page",
      key: "page",
      responsive: ["lg"],
      render: (text) => {
        return getPageText(text);
      },
    },
    {
      title: "Dispositivo",
      dataIndex: "device",
      key: "device",
      responsive: ["lg"],
      render: (text) => {
        const type = text?.type || text;
        switch (type) {
          case "web":
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <DesktopOutlined style={{ marginRight: "8px" }} />
                <Text>Computador</Text>
              </div>
            );
          case "app":
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <MobileOutlined style={{ marginRight: "8px" }} />
                <Text>Aplicativo</Text>
              </div>
            );
          case "server":
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <CloudServerOutlined style={{ marginRight: "8px" }} />
                <Text>Servidor</Text>
              </div>
            );
          default:
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <GlobalOutlined style={{ marginRight: "8px" }} />
                <Text>API</Text>
              </div>
            );
        }
      },
    },
    {
      title: "Conexão",
      dataIndex: "network",
      key: "network",
      responsive: ["lg"],
      valueEnum: {
        offline: {
          text: "Offline",
          status: "Error",
        },
        online: {
          text: "Online",
          status: "Success",
        },
      },
    },
    {
      title: "Ação",
      dataIndex: "action",
      key: "action",
      render: (text) => {
        switch (text) {
          case "CREATE":
            return <Text type="success">{getActionText(text)}</Text>;
          case "UPDATE":
            return <Text type="warning">{getActionText(text)}</Text>;
          case "DELETE":
            return <Text type="danger">{getActionText(text)}</Text>;
          default:
            return text;
        }
      },
    },
    {
      title: "Detalhes",
      dataIndex: "changes",
      key: "changes",
      responsive: ["md"],
      render: (text, record) => {
        if (record.changes && record.changes.length > 0) {
          let size = _.size(_.uniqBy(_.map(record.changes, "id")));
          if (record.metadata && record.metadata.totalCount !== undefined) {
            size = record.metadata.totalCount;
          }
          const action = record?.action;

          switch (action) {
            case "CREATE":
              return `${size === 0 ? "registro" : size === 1 ? "1 registro" : `${size} registros`}`;
            case "UPDATE":
              return `${size === 0 ? "alteração" : size === 1 ? "1 alteração" : `${size} alterações`}`;
            case "DELETE":
              return `${size === 0 ? "remoção" : size === 1 ? "1 remoção" : `${size} remoções`}`;
            default:
              return "N/A";
          }
        } else {
          return "N/A";
        }
      },
    },
    {
      title: "Autor",
      dataIndex: "createdBy",
      key: "createdBy",
      responsive: ["md"],
      render: (text) => {
        if (text === "System") return "Sistema";
        return userNames.get(text) || text || "N/A";
      },
    },
    {
      title: (
        <>
          Criado em
          <Tooltip placement="top" title="Data da atividade">
            <QuestionCircleOutlined style={{ marginLeft: 5 }} />
          </Tooltip>
        </>
      ),
      dataIndex: "timestamp",
      key: "timestamp",
      render: (text) => moment(text).format("DD/MM/YYYY HH:mm:ss"),
      sorter: (a, b) =>
        moment(a.timestamp).unix() -
        moment(b.timestamp).unix(),
    },
    {
      title: "Opções",
      key: "actions",
      render: (text, record) => {
        const canRevert = record.changes && record.changes.length > 0 && !record?.isReverted;

        return (
          <Space size="middle">
            {canRevert && (
              <Button
                danger
                disabled={record.action === "UPDATE"}
                onClick={() => handleRevert(record)}
              >
                Reverter
              </Button>
            )}
            {record?.isReverted && (
              <Button type="link" disabled>
                Revertido
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    return <ActivityDetails record={record} />;
  };

  const rowExpandable = (record) => {
    return record.changes && record.changes.length > 0;
  };

  return (
    <LayoutTopSide>
      <TopBar />
      <Layout style={{ overflow: "hidden" }}>
        <DrawerSider />
        <Layout
          style={{
            padding: "0 24px 24px",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <PageHeader
            style={{ margin: "16px 0 5px 0" }}
            title="Lista de atividades"
            subTitle="Acompanhe o lançamento, edição e exclusão de qualquer registro de formulário."
            breadcrumb={
              <Breadcrumb>
                {routes.map((a) =>
                  a.hasOwnProperty("href") ? (
                    <Breadcrumb.Item key={Math.random()}>
                      <a href onClick={() => navigate(a.href)}>
                        {a.breadcrumbName}
                      </a>
                    </Breadcrumb.Item>
                  ) : (
                    <Breadcrumb.Item>{a.breadcrumbName}</Breadcrumb.Item>
                  )
                )}
              </Breadcrumb>
            }
          />
          <Content
            className="site-layout-background"
            style={{
              padding: 24,
              margin: 0,
              minHeight: "fit-content",
              background: "var(--cp-surface)",
            }}
          >
            <Row gutter={16}>
              {/* Começo dos filtros */}
              <ActivitiesFilters
                activeKeys={activeKeys}
                setActiveKeys={setActiveKeys}
                filters={filters}
              />

              <Col span={24}>
                <ConfigProvider locale={ptBRIntl}>
                  <ProTable
                    actionRef={actionRef}
                    params={filters.filters}
                    pagination={defaultTablePagination}
                    request={async (params = {}) => {
                      try {
                        const { current, pageSize: pSize, ...filterParams } = params;
                        const mergedFilters = { ...filters.filters, ...filterParams };
                        const response = await activityAPI.list({
                          ...mergedFilters,
                          pageNumber: current || 1,
                          pageSize: pSize || 10,
                        });

                        const result = response.data;

                        // Resolve data from different API response shapes
                        let items = [];
                        let total = 0;

                        if (result && typeof result === 'object') {
                          if (Array.isArray(result)) {
                            items = result;
                            total = result.length;
                          } else if (result.data && Array.isArray(result.data)) {
                            items = result.data;
                            total = result.pagination?.total ?? result.data.length;
                          } else if (Array.isArray(result.message)) {
                            items = result.message;
                            total = result.pagination?.total ?? result.message.length;
                          }
                        }

                        // Resolve user names for this page only
                        if (items.length > 0) {
                          const ids = items
                            .map((a) => a.createdBy)
                            .filter((id) => id && id !== "System");
                          if (ids.length > 0) {
                            try {
                              const map = await resolveUserNames(ids, company?.unitId);
                              setUserNames((prev) => {
                                const merged = new Map(prev);
                                map.forEach((val, key) => merged.set(key, val));
                                return merged;
                              });
                            } catch (error) {
                              console.error("Failed to resolve user names", error);
                            }
                          }
                        }

                        setTotalActivities(total);

                        return {
                          data: items,
                          success: true,
                          total,
                        };
                      } catch (error) {
                        console.error('Erro ao buscar atividades:', error);
                        message.error('Ocorreu um erro ao buscar as atividades.');
                        setTotalActivities(0);
                        return {
                          data: [],
                          success: false,
                          total: 0,
                        };
                      }
                    }}
                    id="core-table"
                    style={{ marginTop: '1rem', background: 'var(--cp-surface)' }}
                    headerTitle={`${totalActivities} ${totalActivities === 1 ? 'atividade encontrada' : 'atividades encontradas'}`}
                    tooltip="Acompanhe seus formulários, campos e dispositivos."
                    columns={columns}
                    rowKey="id"
                    cardBordered
                    search={false}
                    scroll={{ x: true }}
                    expandable={{
                      expandedRowRender,
                      rowExpandable,
                      expandedRowKeys,
                      onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
                    }}
                    options={{ reload: true }}
                    locale={{
                      emptyText: <Empty description="Nenhuma atividade adicionada." />,
                    }}
                  />
                </ConfigProvider>
              </Col>
            </Row>
          </Content>
        </Layout>
      </Layout>
    </LayoutTopSide>
  );
}

const mapStateToProps = ({ session }) => ({
  user: session.user,
});

export default connect(mapStateToProps)(AllActivities);
